import {
    IProps,
    IRef,
} from './types';

import classNames from 'classnames';
import React, {
    forwardRef,
    useImperativeHandle,
    useState,
} from 'react';

import DateTime from 'helpers/DateTime';

import DateInput from '../DateInput';
import TimeInput from '../TimeInput';

import {
    ERROR,
} from './constants';
import {
    formatError,
    formatValue,
} from './functions';

import styles from './DateTimeInput.module.scss';

const DateTimeInput = forwardRef<IRef, IProps>((props, ref) => {
    const initialTimestamp = props.value?.getTime() ?? null;
    const [date, setDate] = useState(() => DateTime.convertNumberToDate(initialTimestamp));
    const [time, setTime] = useState(() => DateTime.convertNumberToTime(initialTimestamp));
    const [error, setError] = useState<ERROR | null>(null);

    const emitValue = (newDate: string | null, newTime: string | null) => {
        props.onChangeValue?.(formatValue({
            date: newDate,
            time: newTime,
            isDefaultEndDay: props.isDefaultEndDay ?? false,
        }));
    };

    useImperativeHandle(ref, () => ({
        getValue() {
            return formatValue({
                date,
                time,
                isDefaultEndDay: props.isDefaultEndDay ?? false,
            });
        },
        setValue(value): void {
            setTimeout(() => {
                const timestamp = value?.getTime() ?? null;

                setDate(DateTime.convertNumberToDate(timestamp));
                setTime(DateTime.convertNumberToTime(timestamp));
            }, 300);
        },
        validate(): boolean {
            const value = formatValue({
                date,
                time,
                isDefaultEndDay: props.isDefaultEndDay ?? false,
            });
            const isValid = !props.isFuture || !!value && value.getTime() > Date.now();

            setError(isValid ? null : ERROR.IS_FUTURE_REQUIRED);

            return isValid;
        },
    }));

    return (
        <div className={classNames(styles.dateTimeInput, props.className)}>
            <DateInput
                label={props.label}
                size={props.size}
                value={date}
                isDisabled={props.isDisabled}
                isError={!!error}
                errorMessage={formatError(error)}
                onChangeValue={(value) => {
                    setDate(value);
                    setError(null);
                    emitValue(value, time);
                }}
            />
            <TimeInput
                size={props.size}
                value={time}
                isDisabled={props.isDisabled}
                isError={!!error}
                errorMessage={formatError(error)}
                onChangeValue={(value) => {
                    setTime(value);
                    setError(null);
                    emitValue(date, value);
                }}
            />
        </div>
    );
});

export default DateTimeInput;
